import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { feManagementSystemApi } from '../api/fe-management-system-api';
import {
  api,
  BySourceTweetsData,
  Tweet,
  TweetSyncResponse,
  WindowTweet,
} from '../api/af-social-tracker-api';
import TweetCard, { Tweet as TweetCardTweet, TweetAuthor as TweetCardAuthor } from './TweetCard';
import SocialTweetSourceDetailPanel from './SocialTweetSourceDetailPanel';
import SocialAccountSourceDetailPanel from './SocialAccountSourceDetailPanel';
import {
  createRowTweetBuckets,
  EMPTY_ROW_TWEET_BUCKETS,
  formatStatDelta,
  parseSyncResultBounds,
  type RowTweetBuckets,
  type SyncResultRecord,
  tweetCreatedMs,
} from '../utils/socialSyncDetailHelpers';

type SocialSourceCardModel = {
  cardId: string;
  sourceType: string;
  sourceKey: string;
  syncTargetId?: string;
  syncResults?: SyncResultRecord[];
  accountPinnedTweetId?: string;
  tweetSyncId?: string;
  isLegacyTweetSyncFallback?: boolean;
};

/** Scroll detail table to this sync result row (same `resultIndex` as in `syncResults[]`). */
export type SocialDetailRowFocus = {
  cardId: string;
  resultIndex: number;
  requestId: number;
} | null;

type Props = {
  tokenAddress: string | null;
  selectedSourceCard: SocialSourceCardModel | null;
  /** X_ACCOUNT: pinned tweet exists (sync metadata or loaded card detail). */
  accountHasPinnedTweet: boolean;
  /** From Overview click: select card + scroll to this result row in the detail table. */
  syncResultViewFocus: SocialDetailRowFocus;
  formatTimestampToLocaleString: (value?: string | Date | number) => string;
  getStatusChipClassName: (status?: string) => string;
  onSyncResultsPatched: (cardId: string, nextSyncResults: SyncResultRecord[]) => void;
};

type CommunitySyncRow = {
  resultIndex: number;
  state: string;
  boundsValid: boolean;
  sinceMs: number;
  untilMs: number;
  monitoringSince: Date;
  monitoringUntil: Date;
};

type CommunityBucketCounts = { admin: number; mod: number; hf: number };

const toWindowTweet = (tweet: Tweet): WindowTweet => ({
  id: tweet.id,
  text: tweet.text,
  createdAt: tweet.createdAt,
  createdAtDate: tweet.createdAt,
  author: {
    id: tweet.author.id,
    userName: tweet.author.userName,
    name: tweet.author.name ?? null,
    profilePicture: tweet.author.profilePicture ?? null,
    followers: tweet.author.followers ?? 0,
    label: tweet.author.label ?? null,
    isBlacklisted: false,
  },
  retweetCount: tweet.retweetCount ?? 0,
  replyCount: tweet.replyCount ?? 0,
  likeCount: tweet.likeCount ?? 0,
  quoteCount: tweet.quoteCount ?? 0,
  viewCount: tweet.viewCount ?? 0,
  bookmarkCount: tweet.bookmarkCount ?? 0,
});

const buildLegacyCommunityBySourceData = (
  syncMeta: TweetSyncResponse,
  tweets: Tweet[],
): BySourceTweetsData => {
  const adminTweets: WindowTweet[] = [];
  const modTweets: WindowTweet[] = [];
  const highFollowerTweets: WindowTweet[] = [];
  const otherTweets: WindowTweet[] = [];

  tweets.forEach((tweet) => {
    const normalized = toWindowTweet(tweet);
    const label = (normalized.author.label ?? '').toUpperCase();
    if (label === 'ADMIN') {
      adminTweets.push(normalized);
      return;
    }
    if (label === 'MOD') {
      modTweets.push(normalized);
      return;
    }
    if (label.length > 0) {
      highFollowerTweets.push(normalized);
      return;
    }
    otherTweets.push(normalized);
    highFollowerTweets.push(normalized);
  });

  return {
    tweetSyncId: syncMeta._id,
    syncMeta,
    tweets: {
      adminTweets,
      modTweets,
      highFollowerTweets,
      otherTweets,
    },
    totalTweets: tweets.length,
  };
};

const CommunityStatDeltaCell: React.FC<{
  current: number;
  previous: number | null;
}> = ({ current, previous }) => {
  const { absText, pctText, positive } = formatStatDelta(current, previous);
  const base =
    'block w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-right tabular-nums';
  if (previous === null) {
    return <span className={`${base} text-gray-600`}>—</span>;
  }
  return (
    <span
      className={`${base} ${
        positive === true ? 'text-emerald-400' : positive === false ? 'text-rose-400' : 'text-gray-500'
      }`}
    >
      {absText}
      {pctText ? `\u00A0${pctText}` : ''}
    </span>
  );
};

const CommunityBucketStatsBlock: React.FC<{
  counts: CommunityBucketCounts;
  prev: CommunityBucketCounts | null;
}> = ({ counts, prev }) => {
  const p = prev;
  const labelCls = 'min-w-0 truncate text-left text-gray-500';
  const valueCls =
    'block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-right tabular-nums text-gray-100';
  return (
    <div className="mt-1.5 w-full min-w-0 max-w-full grid grid-cols-3 gap-x-1.5 gap-y-0.5 border-t border-gray-700/80 pt-1.5 text-[10px] leading-snug">
      <span className={labelCls}>Admin</span>
      <span className={valueCls}>{counts.admin.toLocaleString()}</span>
      <CommunityStatDeltaCell current={counts.admin} previous={p?.admin ?? null} />

      <span className={labelCls}>Mod</span>
      <span className={valueCls}>{counts.mod.toLocaleString()}</span>
      <CommunityStatDeltaCell current={counts.mod} previous={p?.mod ?? null} />

      <span className={labelCls}>HF</span>
      <span className={valueCls}>{counts.hf.toLocaleString()}</span>
      <CommunityStatDeltaCell current={counts.hf} previous={p?.hf ?? null} />
    </div>
  );
};

const mapWindowTweetToCardTweet = (windowTweet: WindowTweet): TweetCardTweet => {
  const author: TweetCardAuthor = {
    id: windowTweet.author.id,
    userName: windowTweet.author.userName,
    name: windowTweet.author.name ?? undefined,
    profilePicture: windowTweet.author.profilePicture ?? undefined,
    followers: windowTweet.author.followers ?? undefined,
    label: windowTweet.author.label ?? undefined,
  };
  return {
    id: windowTweet.id,
    text: windowTweet.text,
    createdAt: windowTweet.createdAt,
    author,
    viewCount: windowTweet.viewCount,
    likeCount: windowTweet.likeCount,
    bookmarkCount: windowTweet.bookmarkCount,
    retweetCount: windowTweet.retweetCount,
    quoteCount: windowTweet.quoteCount,
    replyCount: windowTweet.replyCount,
  };
};

const SocialSourceDetailPanel: React.FC<Props> = ({
  tokenAddress,
  selectedSourceCard,
  accountHasPinnedTweet,
  syncResultViewFocus,
  formatTimestampToLocaleString,
  getStatusChipClassName,
  onSyncResultsPatched,
}) => {
  const detailRowFocus = useMemo(() => {
    if (!syncResultViewFocus || !selectedSourceCard) return null;
    if (syncResultViewFocus.cardId !== selectedSourceCard.cardId) return null;
    return {
      resultIndex: syncResultViewFocus.resultIndex,
      requestId: syncResultViewFocus.requestId,
    };
  }, [syncResultViewFocus, selectedSourceCard]);
  const showCommunityTimeline =
    Boolean(selectedSourceCard) &&
    selectedSourceCard?.sourceType?.toUpperCase() === 'X_COMMUNITY';

  const showTweetTimeline =
    Boolean(selectedSourceCard) && selectedSourceCard?.sourceType?.toUpperCase() === 'X_TWEET';

  const showAccountTimeline =
    Boolean(selectedSourceCard) && selectedSourceCard?.sourceType?.toUpperCase() === 'X_ACCOUNT';

  const [localSyncResults, setLocalSyncResults] = useState<SyncResultRecord[]>(
    selectedSourceCard?.syncResults ?? [],
  );
  const [communityAllTweets, setCommunityAllTweets] = useState<BySourceTweetsData | null>(null);
  const [communityRowTweetMap, setCommunityRowTweetMap] =
    useState<Record<number, RowTweetBuckets>>({});
  const [communityPreMonitoringTweets, setCommunityPreMonitoringTweets] =
    useState<RowTweetBuckets>(EMPTY_ROW_TWEET_BUCKETS);
  const [isCommunityTweetsLoading, setIsCommunityTweetsLoading] = useState(false);
  const [hoveredCommunityRowIdx, setHoveredCommunityRowIdx] = useState<number | null>(null);
  const [editingCommunityRowIdx, setEditingCommunityRowIdx] = useState<number | null>(null);
  const [communityNoteDraft, setCommunityNoteDraft] = useState('');
  const [isSavingCommunityNote, setIsSavingCommunityNote] = useState(false);

  /** Avoid re-running fetch when parent only updates `syncResults` after our own patch. */
  const onSyncResultsPatchedRef = useRef(onSyncResultsPatched);
  onSyncResultsPatchedRef.current = onSyncResultsPatched;

  const selectedSourceCardRef = useRef(selectedSourceCard);
  selectedSourceCardRef.current = selectedSourceCard;

  const communityCardId = selectedSourceCard?.cardId ?? null;
  const communitySourceType = selectedSourceCard?.sourceType ?? '';
  const communitySourceKey = (selectedSourceCard?.sourceKey ?? '').trim();
  const isLegacyCommunityFallback = Boolean(
    selectedSourceCard?.isLegacyTweetSyncFallback,
  );

  /** Stable while the same community card is selected (ignores parent object/syncResults reference churn). */
  const communityFetchKey = useMemo(() => {
    if (!communityCardId || communitySourceType.toUpperCase() !== 'X_COMMUNITY') {
      return null;
    }
    if (isLegacyCommunityFallback) {
      return `${communityCardId}::legacy`;
    }
    if (!communitySourceKey) {
      return null;
    }
    return `${communityCardId}::${communitySourceKey}`;
  }, [
    communityCardId,
    communitySourceType,
    communitySourceKey,
    isLegacyCommunityFallback,
  ]);

  useEffect(() => {
    const card = selectedSourceCardRef.current;
    if (!card?.cardId) return;
    setLocalSyncResults(card.syncResults ?? []);
    setCommunityAllTweets(null);
    setCommunityRowTweetMap({});
    setCommunityPreMonitoringTweets(EMPTY_ROW_TWEET_BUCKETS);
    setEditingCommunityRowIdx(null);
    setCommunityNoteDraft('');
    setHoveredCommunityRowIdx(null);
  }, [selectedSourceCard?.cardId]);

  const communitySyncResults = useMemo((): CommunitySyncRow[] => {
    if (!showCommunityTimeline || !localSyncResults) return [];
    return localSyncResults
      .map((r, resultIndex) => ({ r, resultIndex }))
      .filter(({ r }) => {
        const state = ((r.state ?? '') as string).toUpperCase();
        return state === 'COMPLETED' || state === 'SYNCING' || state === 'FAILED';
      })
      .map(({ r, resultIndex }) => {
        const bounds = parseSyncResultBounds(r);
        const state = ((r.state ?? '') as string).toUpperCase();
        return {
          resultIndex,
          state,
          boundsValid: bounds != null,
          sinceMs: bounds?.sinceMs ?? NaN,
          untilMs: bounds?.untilMs ?? NaN,
          monitoringSince: bounds ? new Date(bounds.sinceMs) : new Date(NaN),
          monitoringUntil: bounds ? new Date(bounds.untilMs) : new Date(NaN),
        };
      })
      .sort((a, b) => {
        const aT = Number.isFinite(a.sinceMs) ? a.sinceMs : 0;
        const bT = Number.isFinite(b.sinceMs) ? b.sinceMs : 0;
        return bT - aT;
      });
  }, [showCommunityTimeline, localSyncResults]);

  useEffect(() => {
    if (!tokenAddress || !communityFetchKey) return;
    const card = selectedSourceCardRef.current;
    if (!card?.cardId) return;
    const sourceType = (card.sourceType || '').trim();
    const sourceKey = (card.sourceKey || '').trim();
    if (!sourceType) return;
    if (!isLegacyCommunityFallback && !sourceKey) return;
    let cancelled = false;

    const refreshCommunityData = async () => {
      setIsCommunityTweetsLoading(true);
      setCommunityAllTweets(null);
      setCommunityRowTweetMap({});
      setCommunityPreMonitoringTweets(EMPTY_ROW_TWEET_BUCKETS);

      try {
        const [targetsResult, bySourceResult] = await Promise.allSettled([
          feManagementSystemApi.listSocialSyncTargets(tokenAddress),
          api.getTweetSyncBySource(tokenAddress, sourceType, sourceKey),
        ]);
        if (cancelled) return;

        if (
          bySourceResult.status === 'fulfilled' &&
          bySourceResult.value?.success &&
          bySourceResult.value.data
        ) {
          setCommunityAllTweets(bySourceResult.value.data);
        } else if (isLegacyCommunityFallback) {
          const [syncMetaResult, communityTweetsResult] = await Promise.allSettled([
            api.getTweetSync(tokenAddress),
            api.getTweetsBySyncId(tokenAddress, true, { page: 1, limit: 500 }),
          ]);
          if (cancelled) return;

          if (
            syncMetaResult.status === 'fulfilled' &&
            communityTweetsResult.status === 'fulfilled'
          ) {
            setCommunityAllTweets(
              buildLegacyCommunityBySourceData(
                syncMetaResult.value,
                communityTweetsResult.value.data ?? [],
              ),
            );
          }
        }

        if (targetsResult.status === 'fulfilled') {
          const freshTargets = targetsResult.value;
          const matchTarget = freshTargets.find((t) => {
            const tType = (t.source_type ?? t.sourceType ?? '').toString().toUpperCase();
            const tKey = (t.source_key ?? t.sourceKey ?? '').toString();
            return tType === sourceType.toUpperCase() && tKey === sourceKey;
          });
          if (matchTarget) {
            const rawResults = (matchTarget.results ?? []) as unknown[];
            const freshSyncResults: SyncResultRecord[] = rawResults
              .filter((r): r is Record<string, unknown> => r != null && typeof r === 'object')
              .map((r) => r as SyncResultRecord);
            setLocalSyncResults(freshSyncResults);
            onSyncResultsPatchedRef.current(card.cardId, freshSyncResults);
          }
        }
      } catch (err) {
        console.error('Community detail refresh 실패:', err);
      } finally {
        if (!cancelled) setIsCommunityTweetsLoading(false);
      }
    };

    void refreshCommunityData();
    return () => {
      cancelled = true;
    };
  }, [tokenAddress, communityFetchKey, isLegacyCommunityFallback]);

  useEffect(() => {
    if (!communityAllTweets || !showCommunityTimeline) return;

    if (communitySyncResults.length === 0) {
      setCommunityRowTweetMap({});
      setCommunityPreMonitoringTweets({
        ...createRowTweetBuckets(true),
        adminTweets: [...communityAllTweets.tweets.adminTweets],
        modTweets: [...communityAllTweets.tweets.modTweets],
        highFollowerTweets: [...communityAllTweets.tweets.highFollowerTweets],
      });
      setIsCommunityTweetsLoading(false);
      return;
    }

    const newRowMap: Record<number, RowTweetBuckets> = {};
    const preBuckets: { admin: WindowTweet[]; mod: WindowTweet[]; hf: WindowTweet[] } = {
      admin: [],
      mod: [],
      hf: [],
    };

    const validRows = communitySyncResults.filter((row) => row.boundsValid);
    const matchOrder = [...validRows].sort((a, b) => b.sinceMs - a.sinceMs);

    const pushByBucket = (tweet: WindowTweet, bucketName: 'admin' | 'mod' | 'hf') => {
      const ms = tweetCreatedMs(tweet);
      if (!Number.isFinite(ms)) {
        preBuckets[bucketName].push(tweet);
        return;
      }

      let placed = false;
      for (const row of matchOrder) {
        if (ms >= row.sinceMs && ms <= row.untilMs) {
          const key = row.resultIndex;
          if (!newRowMap[key]) {
            newRowMap[key] = createRowTweetBuckets(true);
          }
          const bucket = newRowMap[key];
          if (bucketName === 'admin') bucket.adminTweets.push(tweet);
          else if (bucketName === 'mod') bucket.modTweets.push(tweet);
          else bucket.highFollowerTweets.push(tweet);
          placed = true;
          break;
        }
      }
      if (!placed) preBuckets[bucketName].push(tweet);
    };

    communityAllTweets.tweets.adminTweets.forEach((tweet) => pushByBucket(tweet, 'admin'));
    communityAllTweets.tweets.modTweets.forEach((tweet) => pushByBucket(tweet, 'mod'));
    communityAllTweets.tweets.highFollowerTweets.forEach((tweet) => pushByBucket(tweet, 'hf'));

    for (const row of communitySyncResults) {
      if (!newRowMap[row.resultIndex]) {
        newRowMap[row.resultIndex] = createRowTweetBuckets(true);
      }
    }

    setCommunityRowTweetMap(newRowMap);
    setCommunityPreMonitoringTweets({
      ...createRowTweetBuckets(true),
      adminTweets: preBuckets.admin,
      modTweets: preBuckets.mod,
      highFollowerTweets: preBuckets.hf,
    });
    setIsCommunityTweetsLoading(false);
  }, [communityAllTweets, communitySyncResults, showCommunityTimeline]);

  const chronologicalCommunityRows = useMemo(() => {
    const valid = communitySyncResults.filter((r) => r.boundsValid);
    return [...valid].sort((a, b) => a.sinceMs - b.sinceMs);
  }, [communitySyncResults]);

  const prevCommunityResultIndexByResultIndex = useMemo(() => {
    const m = new Map<number, number | null>();
    chronologicalCommunityRows.forEach((row, j) => {
      m.set(row.resultIndex, j === 0 ? null : chronologicalCommunityRows[j - 1].resultIndex);
    });
    return m;
  }, [chronologicalCommunityRows]);

  const handleStartCommunityNoteEdit = useCallback((resultIndex: number) => {
    const existingNote = ((localSyncResults ?? [])[resultIndex]?.note ?? '') as string;
    setEditingCommunityRowIdx(resultIndex);
    setCommunityNoteDraft(existingNote.trim());
  }, [localSyncResults]);

  const handleCancelCommunityNoteEdit = useCallback(() => {
    setEditingCommunityRowIdx(null);
    setCommunityNoteDraft('');
  }, []);

  const handleSubmitCommunityNote = useCallback(async () => {
    if (editingCommunityRowIdx === null || isSavingCommunityNote || !selectedSourceCard?.syncTargetId) return;
    setIsSavingCommunityNote(true);
    try {
      await feManagementSystemApi.updateSyncResultNote(
        selectedSourceCard.syncTargetId,
        editingCommunityRowIdx,
        communityNoteDraft,
      );
      const updated = [...(localSyncResults ?? [])];
      if (updated[editingCommunityRowIdx]) {
        updated[editingCommunityRowIdx] = {
          ...updated[editingCommunityRowIdx],
          note: communityNoteDraft,
        };
      }
      setLocalSyncResults(updated);
      onSyncResultsPatched(selectedSourceCard.cardId, updated);
      setEditingCommunityRowIdx(null);
      setHoveredCommunityRowIdx(null);
      setCommunityNoteDraft('');
    } catch (err) {
      console.error('Community note 저장 실패:', err);
    } finally {
      setIsSavingCommunityNote(false);
    }
  }, [
    editingCommunityRowIdx,
    isSavingCommunityNote,
    selectedSourceCard,
    communityNoteDraft,
    localSyncResults,
    onSyncResultsPatched,
  ]);

  const communityRowFocusRefs = useRef<Record<number, HTMLDivElement | null>>({});

  useLayoutEffect(() => {
    if (!showCommunityTimeline || !detailRowFocus || isCommunityTweetsLoading) return;
    const idx = detailRowFocus.resultIndex;
    if (!communitySyncResults.some((r) => r.resultIndex === idx)) return;
    const id = requestAnimationFrame(() => {
      communityRowFocusRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(id);
  }, [showCommunityTimeline, detailRowFocus, isCommunityTweetsLoading, communitySyncResults]);

  if (!selectedSourceCard) {
    return (
      <div className="p-6 text-center text-sm text-gray-400">
        소셜 소스 카드를 선택하면 하단에 상세가 표시됩니다.
      </div>
    );
  }

  if (showTweetTimeline) {
    return (
      <SocialTweetSourceDetailPanel
        tokenAddress={tokenAddress}
        selectedSourceCard={selectedSourceCard}
        rowFocusTarget={detailRowFocus}
        formatTimestampToLocaleString={formatTimestampToLocaleString}
        getStatusChipClassName={getStatusChipClassName}
        onSyncResultsPatched={onSyncResultsPatched}
      />
    );
  }

  if (showAccountTimeline) {
    return (
      <SocialAccountSourceDetailPanel
        tokenAddress={tokenAddress}
        selectedSourceCard={selectedSourceCard}
        accountHasPinnedTweet={accountHasPinnedTweet}
        rowFocusTarget={detailRowFocus}
        formatTimestampToLocaleString={formatTimestampToLocaleString}
        getStatusChipClassName={getStatusChipClassName}
        onSyncResultsPatched={onSyncResultsPatched}
      />
    );
  }

  if (!showCommunityTimeline) {
    return (
      <div className="p-6 text-center text-sm text-gray-400">
        지원하지 않는 소스 타입입니다.
      </div>
    );
  }

  return (
    <>
      {isCommunityTweetsLoading && communitySyncResults.length === 0 ? (
        <div className="p-4 text-sm text-gray-500">Loading community details...</div>
      ) : null}
      <div className="grid grid-cols-[264px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] bg-gray-900/70 border-b border-gray-700 text-xs font-semibold text-gray-300">
        <div className="px-3 py-2 border-r border-gray-700">Time Range</div>
        <div className="px-3 py-2 border-r border-gray-700">Admin Tweets</div>
        <div className="px-3 py-2 border-r border-gray-700">Mod Tweets</div>
        <div className="px-3 py-2">HF Tweets</div>
      </div>

      <div className="divide-y divide-gray-700">
        {communitySyncResults.length === 0 &&
          !isCommunityTweetsLoading &&
          communityAllTweets === null && (
            <div className="p-4 text-sm text-gray-500">No sync results.</div>
          )}
        {communitySyncResults.map((row) => {
          const rowTweets = communityRowTweetMap[row.resultIndex] || EMPTY_ROW_TWEET_BUCKETS;
          const hasAnyTweet =
            rowTweets.highFollowerTweets.length > 0 ||
            rowTweets.adminTweets.length > 0 ||
            rowTweets.modTweets.length > 0;
          const isCompactRow = rowTweets.loaded && !rowTweets.isLoading && !hasAnyTweet;
          const existingNote = ((localSyncResults ?? [])[row.resultIndex]?.note ?? '') as string;
          const isEditingThisRow = editingCommunityRowIdx === row.resultIndex;
          const isHoveringThisRow = hoveredCommunityRowIdx === row.resultIndex;

          return (
            <div
              key={`community-${row.resultIndex}-${row.boundsValid ? row.sinceMs : 'invalid'}`}
              ref={(el) => {
                communityRowFocusRefs.current[row.resultIndex] = el;
              }}
              className={`grid grid-cols-[264px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] text-sm text-gray-300 bg-gray-900/30 ${
                isCompactRow ? 'min-h-[40px]' : 'min-h-[160px]'
              }`}
            >
              <div
                className="relative min-w-0 overflow-x-hidden px-3 py-3 border-r border-gray-700 text-xs leading-tight"
                onMouseEnter={() => setHoveredCommunityRowIdx(row.resultIndex)}
                onMouseLeave={() => setHoveredCommunityRowIdx(null)}
              >
                <div>
                  {row.boundsValid
                    ? formatTimestampToLocaleString(row.monitoringUntil)
                    : '—'}
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">
                  {row.boundsValid
                    ? `${formatTimestampToLocaleString(row.monitoringSince)} ~`
                    : 'Invalid range'}
                </div>
                {row.state && (
                  <span className={`mt-1 inline-block rounded border px-1.5 py-0.5 text-[10px] font-medium ${getStatusChipClassName(row.state)}`}>
                    {row.state}
                  </span>
                )}
                {rowTweets.loaded && !rowTweets.isLoading ? (
                  <CommunityBucketStatsBlock
                    counts={{
                      admin: rowTweets.adminTweets.length,
                      mod: rowTweets.modTweets.length,
                      hf: rowTweets.highFollowerTweets.length,
                    }}
                    prev={(() => {
                      const prevIdx = prevCommunityResultIndexByResultIndex.get(row.resultIndex) ?? null;
                      if (prevIdx == null) return null;
                      const pb = communityRowTweetMap[prevIdx];
                      if (!pb) return null;
                      return {
                        admin: pb.adminTweets.length,
                        mod: pb.modTweets.length,
                        hf: pb.highFollowerTweets.length,
                      };
                    })()}
                  />
                ) : null}
                {isHoveringThisRow && !isEditingThisRow && (
                  <button
                    type="button"
                    className="absolute top-1 right-1 rounded bg-gray-700 px-1.5 py-0.5 text-[10px] text-gray-300 hover:bg-gray-600 transition-colors"
                    onClick={() => handleStartCommunityNoteEdit(row.resultIndex)}
                  >
                    ✎
                  </button>
                )}
                {existingNote && !isEditingThisRow && (
                  <div className="mt-2 rounded bg-gray-800/70 px-2 py-1.5 text-[11px] text-gray-300 whitespace-pre-wrap break-words leading-relaxed">
                    {existingNote}
                  </div>
                )}
                {isEditingThisRow && (
                  <div className="mt-2 space-y-1">
                    <textarea
                      className="w-full rounded border border-gray-600 bg-gray-800 px-2 py-1 text-[11px] text-gray-200 outline-none focus:border-blue-500 resize-none"
                      rows={3}
                      value={communityNoteDraft}
                      onChange={(e) => setCommunityNoteDraft(e.target.value)}
                      placeholder="메모 입력..."
                      disabled={isSavingCommunityNote}
                    />
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="rounded bg-blue-600 px-2 py-0.5 text-[10px] text-white hover:bg-blue-500 disabled:opacity-50 transition-colors"
                        onClick={handleSubmitCommunityNote}
                        disabled={isSavingCommunityNote}
                      >
                        {isSavingCommunityNote ? '...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        className="rounded bg-gray-700 px-2 py-0.5 text-[10px] text-gray-300 hover:bg-gray-600 transition-colors"
                        onClick={handleCancelCommunityNoteEdit}
                        disabled={isSavingCommunityNote}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="min-w-0 px-3 py-3 border-r border-gray-700">
                {rowTweets.isLoading ? (
                  <div className="text-xs text-gray-500">Loading...</div>
                ) : rowTweets.adminTweets.length === 0 ? (
                  <div className="text-gray-500 text-xs">-</div>
                ) : (
                  <div className="space-y-2 min-w-0">
                    {rowTweets.adminTweets.map((tweet, idx) => (
                      <TweetCard
                        key={`c-admin-${row.resultIndex}-${tweet.id}-${idx}`}
                        tweet={mapWindowTweetToCardTweet(tweet)}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="min-w-0 px-3 py-3 border-r border-gray-700">
                {rowTweets.isLoading ? (
                  <div className="text-xs text-gray-500">Loading...</div>
                ) : rowTweets.modTweets.length === 0 ? (
                  <div className="text-gray-500 text-xs">-</div>
                ) : (
                  <div className="space-y-2 min-w-0">
                    {rowTweets.modTweets.map((tweet, idx) => (
                      <TweetCard
                        key={`c-mod-${row.resultIndex}-${tweet.id}-${idx}`}
                        tweet={mapWindowTweetToCardTweet(tweet)}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="min-w-0 px-3 py-3">
                {rowTweets.isLoading ? (
                  <div className="text-xs text-gray-500">Loading...</div>
                ) : rowTweets.highFollowerTweets.length === 0 ? (
                  <div className="text-gray-500 text-xs">-</div>
                ) : (
                  <div className="space-y-2 min-w-0">
                    {rowTweets.highFollowerTweets.map((tweet, idx) => (
                      <TweetCard
                        key={`c-hf-${row.resultIndex}-${tweet.id}-${idx}`}
                        tweet={mapWindowTweetToCardTweet(tweet)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {(communitySyncResults.length > 0 || communityAllTweets !== null) && (
          <div className="grid min-h-[120px] grid-cols-[264px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] text-sm text-gray-300 bg-gray-900/30">
            <div className="px-3 py-3 border-r border-gray-700 text-xs leading-tight">
              <div>모니터링 이전 트윗들</div>
            </div>
            <div className="min-w-0 px-3 py-3 border-r border-gray-700">
              {communityPreMonitoringTweets.isLoading ? (
                <div className="text-xs text-gray-500">Loading...</div>
              ) : communityPreMonitoringTweets.adminTweets.length === 0 ? (
                <div className="text-gray-500 text-xs">-</div>
              ) : (
                <div className="space-y-2 min-w-0">
                  {communityPreMonitoringTweets.adminTweets.map((tweet, idx) => (
                    <TweetCard
                      key={`cpre-admin-${tweet.id}-${idx}`}
                      tweet={mapWindowTweetToCardTweet(tweet)}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="min-w-0 px-3 py-3 border-r border-gray-700">
              {communityPreMonitoringTweets.isLoading ? (
                <div className="text-xs text-gray-500">Loading...</div>
              ) : communityPreMonitoringTweets.modTweets.length === 0 ? (
                <div className="text-gray-500 text-xs">-</div>
              ) : (
                <div className="space-y-2 min-w-0">
                  {communityPreMonitoringTweets.modTweets.map((tweet, idx) => (
                    <TweetCard
                      key={`cpre-mod-${tweet.id}-${idx}`}
                      tweet={mapWindowTweetToCardTweet(tweet)}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="min-w-0 px-3 py-3">
              {communityPreMonitoringTweets.isLoading ? (
                <div className="text-xs text-gray-500">Loading...</div>
              ) : communityPreMonitoringTweets.highFollowerTweets.length === 0 ? (
                <div className="text-gray-500 text-xs">-</div>
              ) : (
                <div className="space-y-2 min-w-0">
                  {communityPreMonitoringTweets.highFollowerTweets.map((tweet, idx) => (
                    <TweetCard
                      key={`cpre-hf-${tweet.id}-${idx}`}
                      tweet={mapWindowTweetToCardTweet(tweet)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SocialSourceDetailPanel;

